"use client";

import Link from "next/link";
import Image from "next/image";
import { Podcast, Clapperboard, FileText, Flame, Rocket, Smile, Heart, Star, ThumbsUp, HandMetal, Sparkles, Brain, Bot, Lightbulb, BrainCircuit } from "lucide-react";
import { RiDoubleQuotesL } from "react-icons/ri";
import { PiTwitterLogo as Twitter, PiLinkedinLogo as Linkedin, PiGithubLogo as Github, PiPlayCircleFill } from "react-icons/pi";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
import { SpotlightItem } from "@/data/spotlight";

// Video placeholder component (now integrated into this file)
function VideoPlaceholder({ youtubeId }: { youtubeId: string }) {
  return (
    <Link 
      href={`https://www.youtube.com/watch?v=${youtubeId}`}
      target="_blank"
      className="block w-full relative aspect-video rounded-lg overflow-hidden group border border-neutral-300 dark:border-neutral-600"
    >
      <div className="absolute inset-0 bg-black/40 flex items-center z-10 justify-center">
        <PiPlayCircleFill 
          className="w-20 h-20 text-white opacity-90 group-hover:scale-90 transition-all duration-200"
        />
      </div>
      <Image
        src={`https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`}
        alt="Video thumbnail"
        width={1280}
        height={720}
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        loading="eager"
        style={{ width: '100%', height: '100%' }}
        className="w-full h-full absolute top-0 left-0 bottom-0 object-cover group-hover:scale-105 transition-all duration-200"
      />
    </Link>
  );
}

interface SpotlightCardProps {
  spotlight: SpotlightItem;
  className?: string;
  id?: string;
}

export default function SpotlightCard({ 
  spotlight, 
  className = "" 
}: SpotlightCardProps) {
  // Note: We no longer need to handle card ID here as it's managed by SpotlightLivingCard
  const { header, content, source } = spotlight;
  
  // Helper function to render the appropriate icon
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case 'twitter':
        return <Twitter className="size-5" />;
      case 'linkedin':
        return <Linkedin className="size-5" />;
      case 'github':
        return <Github className="size-5" />;
      case 'testimonial':
      case 'quote':
        return <RiDoubleQuotesL className="size-6 mb-[-4px]" />;
      case 'video':
        return <Clapperboard className="size-5" />;
      case 'podcast':
        return <Podcast className="size-5" />;
      case 'report':
        return <FileText className="size-5" />;
      case 'fire':
        return <Flame className="size-5" />;
      case 'rocket':
        return <Rocket className="size-5" />;
      case 'smile':
        return <Smile className="size-5" />;
      case 'heart':
        return <Heart className="size-5" />;
      case 'star':
        return <Star className="size-5" />;
      case 'thumbs-up':
        return <ThumbsUp className="size-5" />;
      case 'hand-metal':
        return <HandMetal className="size-5" />;
      case 'sparkles':
        return <Sparkles className="size-5" />;
      case 'brain':
        return <Brain className="size-5" />;
      case 'bot':
        return <Bot className="size-5" />;
      case 'lightbulb':
        return <Lightbulb className="size-5" />;
      case 'brain-circuit':
        return <BrainCircuit className="size-5" />;
      default:
        return null;
    }
  };

  // Check if sections have actual content
  const hasHeader = header && (header.logo || header.icon);
  const hasVisualContent = content.video || content.image;
  const hasTextContent = content.text;
  const hasSource = source && (source.avatar || source.title);

  return (
    <div className={`flex flex-col h-full w-full gap-3 overflow-hidden ${className}`}>
      {/* TOP SECTION - Header */}
      {hasHeader && (
        <div className="spotlight-header">
          <div className="w-full spotlight-header-image">
            {header.logo && (
              <>
                {/* Render logo with or without link wrapper */}
                {(() => {
                  // Extract the common logo elements to avoid duplication
                  const logoElements = (
                    <>
                      {/* Render only one logo element based on the current theme */}
                      {header.logoDark ? (
                        // If we have both light and dark logos, use client-side rendering
                        // with CSS to show/hide appropriate logo
                        <div className="relative flex justify-start w-full">
                          {/* Light mode logo */}
                          <div className="block dark:hidden">
                            <Image
                              className="h-auto w-auto max-h-8 object-contain object-left"
                              src={header.logo}
                              alt="Company logo"
                              height={40}
                              width={160}
                              loading="eager"
                            />
                          </div>
                          
                          {/* Dark mode logo */}
                          <div className="hidden dark:block">
                            <Image
                              className="h-auto w-auto max-h-8 object-contain object-left"
                              src={header.logoDark}
                              alt="Company logo"
                              height={40}
                              width={160}
                              loading="eager"
                            />
                          </div>
                        </div>
                      ) : (
                        /* Only one logo for both modes */
                        <Image
                          className={`h-auto w-auto max-h-8 object-contain object-left ${
                            header.preserveImageColors ? 'preserve-colors' : ''
                          }`}
                          src={header.logo}
                          alt="Company logo"
                          height={40}
                          width={160}
                          loading="eager"
                        />
                      )}
                    </>
                  );

                  // Wrap with Link if URL exists
                  return header.url ? (
                    <Link href={header.url} target="_blank">
                      {logoElements}
                    </Link>
                  ) : logoElements;
                })()}
              </>
            )}

            {/* Icon (displayed only if no logo is present) */}
            {!header.logo && header.icon && (
              header.url ? (
                <Link href={header.url} target="_blank" className="text-muted-foreground hover:text-accent transition-colors duration-300">
                  {renderIcon(header.icon)}
                </Link>
              ) : (
                <div className="text-muted-foreground transition-colors duration-300">
                  {renderIcon(header.icon)}
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* MIDDLE SECTION - Content (both visual and text) */}
      <div className="spotlight-content flex-1 min-h-0 overflow-hidden">
        {/* Use independent containers that each take full width */}
        <div className="w-full flex flex-col items-stretch gap-3">
          {/* Visual content (video or image) */}
          {hasVisualContent && (
            <div className="spotlight-visual-content w-full">
              {/* Video content */}
              {content.video && (
                <div className="w-full">
                  <VideoPlaceholder youtubeId={content.video} />
                </div>
              )}

              {/* Content image */}
              {content.image && !content.video && (
                <div className={`w-full ${!hasTextContent ? 'flex-1 flex items-center justify-center' : ''}`}>
                  {content.url ? (
                    <Link href={content.url} target="_blank" className="block w-full">
                      <Image
                        className={`${!hasTextContent ? 'w-full h-full object-cover' : 'h-auto max-w-full object-contain'} rounded-lg border border-neutral-300 dark:border-neutral-600 ${header?.preserveImageColors ? 'preserve-colors' : ''}`}
                        src={content.image}
                        alt="Content image"
                        height={200}
                        width={300}
                        loading="eager"
                        sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
                      />
                    </Link>
                  ) : (
                    <Image
                      className={`${!hasTextContent ? 'w-full h-full object-cover' : 'h-auto max-w-full object-contain'} rounded-lg border border-neutral-300 dark:border-neutral-600 ${header?.preserveImageColors ? 'preserve-colors' : ''}`}
                      src={content.image}
                      alt="Content image"
                      height={200}
                      width={300}
                      loading="eager"
                      sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* Text content - in its own flex row that takes full width */}
          {hasTextContent && (
            <div className="spotlight-text-container overflow-y-auto w-full flex-1 min-h-0 max-h-full">
              <div className="w-full flex flex-col">
                {content.url || content.video ? (
                  <Link
                    href={content.url || `https://www.youtube.com/watch?v=${content.video}`}
                    target="_blank"
                    className="block hover:text-foreground/80 w-full"
                  >
                    <MarkdownRenderer 
                      content={content.text || ""} 
                      className="inline w-full"
                    />
                  </Link>
                ) : (
                  <div className="w-full">
                    <MarkdownRenderer 
                      content={content.text || ""} 
                      className="w-full text-wrap"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* BOTTOM SECTION - Only render if source info exists */}
      {hasSource && (
        <div className="flex-none mt-auto">
          {/* Source information (avatar, title, subtitle) */}
          <div className="grid grid-cols-[auto_1fr] items-center gap-3 w-full">
            {source.avatar && (
              <Avatar className="size-10 flex-shrink-0">
                <Image
                  src={source.avatar}
                  alt={source.title || "Profile picture"}
                  width={40}
                  height={40}
                  sizes="40px"
                  loading="eager"
                  className="aspect-square size-full rounded-full"
                />
                <AvatarFallback>{source.title?.slice(0, 2).toUpperCase() || "ML"}</AvatarFallback>
              </Avatar>
            )}
            {(source.title || source.subtitle) && (
              <div className="overflow-hidden min-w-0">
                {source.url ? (
                  <Link href={source.url} target="_blank" className="hover:text-foreground/80 block">
                    {source.title && <p className="font-medium text-sm truncate">{source.title}</p>}
                    {source.subtitle && <span className="text-muted-foreground block text-xs truncate">{source.subtitle}</span>}
                  </Link>
                ) : (
                  <>
                    {source.title && <p className="font-medium text-sm truncate">{source.title}</p>}
                    {source.subtitle && <span className="text-muted-foreground block text-xs truncate">{source.subtitle}</span>}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}